import type { Order, OrderItem, OrderPrices, Product } from "@jti/magento";
import type { CartSliceState } from "@jti/store/slices/cartSlice";
import type { FreeTrialCart, FreeTrialPrice } from "@jti/store/types";
import { mapProductToCommerceEventItem } from "@jti/store/utils";
import type {
  CommerceEventBaseData,
  CommerceEventItem,
  SharedData,
  TrackingEvents,
  TrackingEventsAdditionalData,
} from "@jti/tracker";

const buildCommerceItemsArray = (arrayWithCartItems?: (Product | OrderItem)[]): CommerceEventItem[] => {
  if (!arrayWithCartItems?.length) {
    return [];
  }

  return arrayWithCartItems.reduce<CommerceEventItem[]>((acc, item, index) => {
    if (!item) {
      return acc;
    }

    acc.push(mapProductToCommerceEventItem(item, index));

    return acc;
  }, []);
};

const getTradeInValue = (orderItems?: (Product | OrderItem)[]) => {
  if (!orderItems) {
    return 0;
  }

  return orderItems.reduce((sum, item) => {
    if (item.price.value) {
      return sum + item.price.value;
    }

    return sum;
  }, 0);
};

const getPurchaseValue = (changedItemValue?: number, prices?: OrderPrices | FreeTrialPrice | null) => {
  const value = changedItemValue
    ? changedItemValue
    : prices && ("total" in prices ? prices.total : prices.grandTotal.value);

  return Number(value);
};

const getCommerceBaseValue = (
  trackingData: CartSliceState | FreeTrialCart | Order,
  orderItems?: (Product | OrderItem)[],
  changedItemValue?: number,
  isTradeInPurchase?: boolean
): number => {
  let prices;

  if (trackingData.prices && "data" in trackingData.prices) {
    prices = trackingData.prices.data;
  } else {
    prices = trackingData.prices;
  }

  return isTradeInPurchase ? getTradeInValue(orderItems) : getPurchaseValue(changedItemValue, prices);
};

export const buildCommerceBaseData = (
  trackingData: CartSliceState | FreeTrialCart | Order,
  changedItemValue?: number,
  isTradeInPurchase?: boolean
): CommerceEventBaseData => {
  const orderItems = isTradeInPurchase
    ? trackingData.items?.filter((orderItem) => orderItem.tradeIn)
    : trackingData.items;

  return {
    currency:
      "currency" in trackingData ? trackingData.currency.code : trackingData.prices?.grandTotal.currency || "N/A",
    value: getCommerceBaseValue(trackingData, orderItems, changedItemValue, isTradeInPurchase),
    ecommerce: {
      items: buildCommerceItemsArray(orderItems),
    },
  };
};

export const buildTradeInPurchaseData = ({
  order,
  sharedData,
  isTradeInPurchase,
}: {
  order: Order;
  sharedData: SharedData;
  isTradeInPurchase?: boolean;
}): TrackingEventsAdditionalData[TrackingEvents.TrackingOrderTradeIn] => {
  const { currency, value, ecommerce } = buildCommerceBaseData(order, undefined, isTradeInPurchase);

  const purchaseData: TrackingEventsAdditionalData[TrackingEvents.TrackingOrderTradeIn] = {
    section: sharedData.section,
    checkout_type: sharedData.checkout_type,
    template_name: "order-confirmation",
    currency,
    value,
    ecommerce,
  };

  return purchaseData;
};

export const buildPurchaseData = ({
  order,
  sharedData,
}: {
  order: Order;
  sharedData: SharedData;
}): TrackingEventsAdditionalData[TrackingEvents.TrackingOrderSummaryPurchase] => {
  const { currency, value, ecommerce } = buildCommerceBaseData(order);

  const purchaseData: TrackingEventsAdditionalData[TrackingEvents.TrackingOrderSummaryPurchase] = {
    section: sharedData.section,
    checkout_type: sharedData.checkout_type,
    template_name: sharedData.template_name,
    currency,
    value,
    ecommerce: {
      ...ecommerce,
      coupon: "N/A",
      shipping: order.shippingMethod?.methodCode ?? "N/A",
      tax: String(order.prices?.appliedTaxes?.[0]?.amount?.value || "N/A"),
      value,
      transaction_id: order.orderId,
    },
  };

  return purchaseData;
};
